import { fetchFacesAlerts } from '@/libraries/api/facesAlerts'
import { imageForDetectionID } from '@/libraries/api/faces'
import moment from 'moment'
import { Service, Operation } from '@/libraries/permissions'
import { DetectionMerger } from '@/libraries/util/detectionMerger'

const mergerOptions = {
  groupByFace: true,
  keys: {
    cameraDescription: 'cameraDescription',
    faceUUID: 'frontendFaceUUID',
    timestamp: 'unixTimestamp',
  },
}

export const ERModule = {
  namespaced: true,
  state: {
    alerts: [],
    lastRefresh: moment().format('h:mm:ss a'),
  },
  mutations: {
    clearAlerts (state) {
      state.lastRefresh = moment().format('h:mm:ss a')
      state.alerts = []
    },
    /**
     * Fetches the newest alerts.
     *
     * @param {object} state The state of the store.
     * @param {array} data A list of alert objects.
     */
    updateAlerts (state, data) {
      state.alerts = data
      state.lastRefresh = moment().format('h:mm:ss a')
    },
  },
  getters: {
    alerts (state) {
      return state.alerts
    },
    lastRefresh (state) {
      return state.lastRefresh
    },
  },
  actions: {
    /**
     * Checks to see if there is an ER in the most recent detections
     */
    checkMostRecent ({ commit, rootGetters, dispatch }) {
      return new Promise((resolve, reject) => {
        const canPerformOperation =
        rootGetters['entitlements/canPerformOperationForService']
        const canAccessElopement = canPerformOperation(
          Service.ALERTING,
          Operation.RECEIVE_ER_ALERTS
        )
        if (!canAccessElopement) {
          return
        }
        const aDayAgo = moment()
          .subtract(24, 'hours')

        const params = {
          domainUUID: rootGetters.currentDomainUUID,
          since: aDayAgo.format(),
          sort: 'ascending',
          include_dismissed: false,
        }

        fetchFacesAlerts(params)
          .then(res => res.data)
          .then(data => {
            if (!data.ok || !data.alerts) {
              reject(new Error(`No faces alerts.`))
            }
            return data.alerts
          })
          .then(alerts => Promise.all(alerts.map(alert => dispatch('transformAlert', alert))))
          .then(alerts => alerts.filter(alert => alert.unixTimestamp > aDayAgo.valueOf()))
          .then(alerts => dispatch('mergeAlerts', { alerts, options: mergerOptions }))
          .then(alerts => alerts.sort((a, b) => a.unixTimestamp < b.unixTimestamp))
          .then(alerts => commit('updateAlerts', alerts))
          .catch(error => console.log(`Error: `, error))
      })
    },
    transformAlert ({ commit, state }, alert) {
      return new Promise((resolve, reject) => {
        // Pull out some things we need.
        const timestamp = moment.utc(alert.event_ts)
        const isElopementAlert = alert.category === 'elopement_alert'
        const detectionId = alert.detection_id

        if (isElopementAlert) {
          const detection = {
            category: alert.category,
            alertId: alert.alert_id,
            unixTimestamp: timestamp.valueOf(),
            posterFrame: imageForDetectionID(detectionId),
            fullFrame: `${imageForDetectionID(detectionId)}?cropped=0&box=1`,
            cameraDescription: alert.description,
            cameraUUID: alert.camera_uuid,
            frontendFaceUUID: alert.frontend_face_uuid,
            detectionId: detectionId,
            lastSeenTS: timestamp,
            dismissedAt: alert.dismissed_at,
            dismissedBy: alert.dismissed_by_end_user_info,
          }
          resolve(detection)
        } else {
          reject(new Error(`Improperly formatted detection...`))
        }
      })
    },
    /**
     * Takes an array of alerts and groups them, based on some options
     *
     * @param {*} { commit, state }
     * @param {*} data
     * @returns
     */
    mergeAlerts ({ commit, state }, { alerts, options }) {
      return new Promise((resolve, reject) => {
        resolve(new DetectionMerger(options).groupedDetectionsFromDetections(alerts))
      })
    },
    incomingElopementAlert ({ state, dispatch, commit }, { alert }) {
      return new Promise((resolve, reject) => resolve(dispatch('transformAlert', alert)))
        .then(alert => state.alerts.concat([alert]))
        .then(alerts => alerts.sort((a, b) => a.unixTimestamp < b.unixTimestamp))
        .then(alerts => dispatch('mergeAlerts', { alerts, options: mergerOptions }))
        .then(alerts => alerts.sort((a, b) => a.unixTimestamp < b.unixTimestamp))
        .then(alerts => commit('updateAlerts', alerts))
        .catch(error => console.log(`Error: `, error))
    },
    clearAlertsData ({ state, commit }) {
      commit('clearAlerts')
    },
  },
}
